import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Eye, Clock, ArrowLeft, Youtube, Share2, Music2 } from "lucide-react";
import { getVideo, getRelated, formatViews, timeAgo } from "@/lib/api";
import VideoCard from "@/components/VideoCard";

const Watch = () => {
  const { videoId } = useParams();
  const [video, setVideo] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    setVideo(null);
    Promise.all([getVideo(videoId), getRelated(videoId)])
      .then(([v, r]) => {
        setVideo(v);
        setRelated(r);
      })
      .catch((err) => { console.error("Failed to load video:", err); })
      .finally(() => setLoading(false));
  }, [videoId]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: video?.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }
    } catch (err) {
      console.error("Share failed:", err);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-8" data-testid="watch-loading">
        <div className="aspect-video-yt bg-[var(--bg-card)] rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!video) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="watch-not-found">
        <h2 className="font-display text-3xl mb-3">Vídeo não encontrado</h2>
        <Link to="/" className="pill pill-primary mt-4 inline-flex">Voltar ao início</Link>
      </div>
    );
  }

  const isShort = video.is_short;

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-6" data-testid="watch-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-[var(--text-dim)] hover:text-white transition-colors mb-4"
        data-testid="back-to-home"
      >
        <ArrowLeft className="w-4 h-4" /> Todos os vídeos
      </Link>

      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        {/* Main column */}
        <div>
          <div
            className={`relative rounded-2xl overflow-hidden bg-black border border-[var(--border)] ${
              isShort ? "aspect-short max-w-[420px] mx-auto" : "aspect-video-yt"
            }`}
            data-testid="video-player-wrapper"
          >
            <iframe
              src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1&hl=pt`}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              data-testid="youtube-iframe"
            />
          </div>

          {/* Title + meta */}
          <div className="mt-5">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold leading-tight" data-testid="video-title">
              {video.title}
            </h1>
            <div className="mt-3 flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <img
                  src="https://yt3.googleusercontent.com/L2SUfYI5Nn0Mt5X43DLmaMCB5Mj-jMdpJW6yVFDP7Rqep9IW9cPzXQ8BP6GiQflyhS1yOZlg8w=s900-c-k-c0x00ffffff-no-rj"
                  alt="Abel Andrade"
                  className="w-11 h-11 rounded-full border border-[var(--border)]"
                />
                <div>
                  <div className="font-medium text-white text-sm">Abel Andrade</div>
                  <div className="text-xs text-[var(--text-muted)]">Criações com alma e coração</div>
                </div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={share} className="pill" data-testid="share-button">
                  <Share2 className="w-4 h-4" />
                  {copied ? "Copiado!" : "Partilhar"}
                </button>
                <a
                  href={`https://www.youtube.com/watch?v=${video.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="pill pill-primary"
                  data-testid="open-on-youtube"
                >
                  <Youtube className="w-4 h-4" /> YouTube
                </a>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3 text-xs text-[var(--text-muted)]">
              <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {formatViews(video.views)} visualizações</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {timeAgo(video.published)}</span>
              {isShort && (
                <>
                  <span>·</span>
                  <span className="tag bg-[var(--accent)] text-white"><Music2 className="w-3 h-3" /> Short</span>
                </>
              )}
            </div>

            <div className="mt-5 p-4 sm:p-5 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)]" data-testid="video-description">
              <h3 className="text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">Descrição</h3>
              <p className="text-sm text-[var(--text-dim)] whitespace-pre-wrap leading-relaxed">
                {video.description}
              </p>
            </div>
          </div>
        </div>

        {/* Sidebar related */}
        <aside data-testid="related-sidebar">
          <h3 className="font-display text-xl font-semibold mb-4">Mais do canal</h3>
          <div className="grid gap-5">
            {related.slice(0, 8).map((v) => (
              <CompactCard key={v.id} video={v} />
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
};

const CompactCard = ({ video }) => (
  <Link
    to={`/watch/${video.id}`}
    className="flex gap-3 group"
    data-testid={`related-${video.id}`}
  >
    <div className="w-40 shrink-0 aspect-video-yt rounded-lg overflow-hidden bg-[var(--bg-card)] relative">
      <img
        src={video.thumbnail}
        alt={video.title}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      />
      {video.is_short && (
        <span className="absolute top-1 left-1 text-[10px] uppercase tracking-wider bg-[var(--accent)] text-white px-1.5 py-0.5 rounded">Short</span>
      )}
    </div>
    <div className="min-w-0 flex-1">
      <h4 className="text-sm font-semibold text-white clamp-2 group-hover:text-[var(--accent)] transition-colors leading-snug">
        {video.title}
      </h4>
      <div className="text-xs text-[var(--text-muted)] mt-1.5">
        {formatViews(video.views)} visualizações · {timeAgo(video.published)}
      </div>
    </div>
  </Link>
);

export default Watch;
